<template>
    <section class="pm-card">
        <!-- Header -->
        <div class="pm-header">
            <i class="fal fa-comment-alt text-blue-500"></i>
            <h2 class="text-lg leading-6 font-medium text-blue-500 mb-0">
                {{ $t('post.page.messages.exchange') }}
            </h2>
            <span class="pm-badge">{{ post.messages.length }}</span>
        </div>

        <!-- Messages list -->
        <div class="pm-messages">
            <div v-if="post.messages && post.messages.length > 0" id="messages" class="pm-thread">
                <template v-for="message in post.messages">
                    <!-- Citizen message (right) -->
                    <div
                        v-if="(message.author_type === 2 || message.author_type === 4) && displayMessage(message)"
                        :key="`c-${message.id}`"
                        class="pm-msg pm-msg-citizen"
                    >
                        <div class="pm-bubble pm-bubble-citizen">
                            <div class="pm-author">
                                <span v-if="message.author">{{ message.author.name }}</span>
                                <span v-else>{{ $t('post.page.messages.anonymous') }}</span>
                                <time :datetime="message.created_at">
                                    {{ moment(message.created_at).locale($store.state.lang).format('LLL') }}
                                </time>
                            </div>
                            <div v-if="message.content" class="pm-text">
                                <span
                                    v-if="message.content.message && message.content.message.length > 0"
                                    class="break-words"
                                    v-html="displayHtml(message.content.message, true)"
                                ></span>
                                <div v-if="message.content.files" class="pm-inline-files">
                                    <a
                                        v-for="(file, index) in message.content.files"
                                        :key="index"
                                        :href="fileDownloadUrl(file)"
                                        :download="file.name"
                                        class="pm-inline-file"
                                    >
                                        <i class="fal fa-paperclip"></i> {{ file.name }}
                                    </a>
                                </div>
                            </div>
                            <div v-if="message.files" class="pm-attachments">
                                <UploadedFile v-for="file in message.files" :key="file.id" :file="file" />
                            </div>
                        </div>
                        <div class="pm-avatar pm-avatar-citizen">
                            <i class="fal fa-user"></i>
                        </div>
                    </div>

                    <!-- Operator message (left) -->
                    <div
                        v-else-if="displayMessage(message)"
                        :key="`o-${message.id}`"
                        class="pm-msg pm-msg-operator"
                    >
                        <div class="pm-avatar pm-avatar-operator">
                            <i class="fal fa-headset"></i>
                        </div>
                        <div class="pm-bubble pm-bubble-operator">
                            <div class="pm-author">
                                <span v-if="message.author">{{ message.author.name }}</span>
                                <span v-else>{{ $t('post.page.messages.anonymous') }}</span>
                                <time :datetime="message.created_at">
                                    {{ moment(message.created_at).locale($store.state.lang).format('LLL') }}
                                </time>
                            </div>
                            <div v-if="message.content" class="pm-text" id="message">
                                <span
                                    v-if="message.content.message"
                                    class="break-words"
                                    v-html="displayHtml(message.content.message, true)"
                                ></span>
                                <div v-if="message.content.files" class="pm-inline-files">
                                    <a
                                        v-for="(file, index) in message.content.files"
                                        :key="index"
                                        :href="fileDownloadUrl(file)"
                                        :download="file.name"
                                        class="pm-inline-file pm-inline-file-light"
                                    >
                                        <i class="fal fa-paperclip"></i> {{ file.name }}
                                    </a>
                                </div>
                            </div>
                            <div v-if="message.files" class="pm-attachments">
                                <UploadedFile v-for="file in message.files" :key="file.id" :file="file" />
                            </div>
                            <div class="pm-read">
                                <template v-if="message.read_at">
                                    <i class="fal fa-check-double"></i>
                                    {{ $t('post.page.messages.read') }}
                                    {{ moment(message.read_at).locale($store.state.lang).format('LLL') }}
                                </template>
                                <template v-else>
                                    <i class="fal fa-check"></i>
                                    {{ $t('post.page.messages.not_read') }}
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <!-- Empty -->
            <div v-else class="pm-empty">
                <i class="fal fa-comment-slash"></i>
                <p>{{ $t('post.page.messages.no_exchange') }}</p>
            </div>
        </div>

        <!-- Compose -->
        <div class="pm-compose">
            <div id="ckComments" class="pm-editor">
                <ckeditor
                    v-model="content"
                    :config="editorConfig"
                    :editor="editor"
                    @input="onChange()"
                />
            </div>
            <div class="pm-toolbar">
                <input
                    id="uploadFiles"
                    ref="fileInput"
                    type="file"
                    multiple
                    class="pm-file-input"
                    @change="onFileInput"
                />

                <BmvButton
                    id="postComment"
                    variant="primary"
                    size="md"
                    icon="fal fa-paper-plane"
                    :loading="loading"
                    :disabled="isDisabled"
                    class="pm-tool pm-tool-send"
                    @click="postComment"
                >
                    {{ loading ? $t('post.page.messages.process') : $t('post.page.messages.send') }}
                </BmvButton>

                <BmvButton
                    variant="primary"
                    size="md"
                    icon="fal fa-paperclip"
                    :disabled="loading"
                    class="pm-tool"
                    @click="openFilePicker"
                >
                    <span class="pm-tool-lbl">
                        {{ $t('post.page.messages.add_attachments') }}
                    </span>
                    <span v-if="filesList.length > 0" class="pm-tool-count">
                        {{ filesList.length }}
                    </span>
                </BmvButton>

                <PostTemplate class="pm-tool-group" @change-content="setContent" />
            </div>
        </div>
    </section>
</template>

<style>
#message ul,
#message ul li {
    list-style-type: disc;
}
#message ol,
#message ol li {
    list-style-type: number;
}

#message ol,
#message ul {
    padding-left: 15px !important;
}

@media only screen and (min-width: 768px) and (max-width: 1280px) {
    .long-url {
        max-width: 20em !important;
        width: 20em !important;
    }
}

.long-url {
    display: block !important;
    max-width: 16rem;
    width: 16rem;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.pm-bubble-citizen .pm-text a {
    color: #0891B2 !important;
    text-decoration: underline !important;
    word-break: break-all;
}

.pm-bubble-citizen .pm-text a:hover {
    color: #0e7490 !important;
}

.pm-bubble-operator .pm-text a {
    color: #bfefff !important;
    text-decoration: underline !important;
    font-weight: 600;
    word-break: break-all;
}

.pm-bubble-operator .pm-text a:hover {
    color: #fff !important;
}
</style>

<style scoped>
.pm-file-label {
    cursor: pointer;
    margin: 0;
}

.pm-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.pm-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.pm-header {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
}

.pm-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.pm-header > i {
    font-size: 1rem;
    flex-shrink: 0;
}

.pm-badge {
    margin-left: auto;
    font-size: 0.72rem;
    font-weight: 700;
    color: #3b82f6;
    background: #eff6ff;
    padding: 2px 10px;
    border-radius: 20px;
}

/* Messages area */
.pm-messages {
    padding: 20px 24px;
    max-height: 600px;
    overflow-y: auto;
}

.pm-thread {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.pm-msg {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    max-width: 80%;
}

.pm-msg-citizen {
    align-self: flex-start;
}

.pm-msg-operator {
    align-self: flex-end;
    flex-direction: row-reverse;
}

.pm-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    flex-shrink: 0;
}

.pm-avatar-citizen {
    background: #e0e7ff;
    color: #4f46e5;
}

.pm-avatar-operator {
    background: #eff6ff;
    color: #3b82f6;
}

/* Bubbles */
.pm-bubble {
    border-radius: 16px;
    padding: 12px 16px;
    min-width: 0;
}

.pm-bubble-citizen {
    background: #f1f5f9;
    border-bottom-left-radius: 4px;
}

.pm-bubble-operator {
    background: #06b6d4;
    color: #fff;
    border-bottom-right-radius: 4px;
}

.pm-author {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 0.72rem;
    opacity: 0.65;
}

.pm-author span:first-child {
    font-weight: 700;
}

.pm-text {
    font-size: 0.88rem;
    line-height: 1.65;
    word-break: break-word;
}

.pm-bubble-citizen .pm-text {
    color: #334155;
}


.pm-inline-files {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.pm-inline-file {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: #3b82f6;
    text-decoration: none !important;
}

.pm-inline-file:hover {
    text-decoration: underline !important;
}

.pm-inline-file-light {
    color: #bfdbfe !important;
}

.pm-attachments {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.pm-read {
    margin-top: 6px;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #fff;
    opacity: 0.7;
}

.pm-read i {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.55rem;
    color: #fff !important;
}

/* Empty */
.pm-empty {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
}

.pm-empty i {
    font-size: 2rem;
    display: block;
    margin-bottom: 10px;
    color: #cbd5e1;
}

/* Compose */
.pm-compose {
    border-top: 1px solid #f1f5f9;
    padding: 16px 24px;
    background: #fafbfc;
    border-radius: 0 0 16px 16px;
}

.pm-editor {
    margin-bottom: 12px;
}

::v-deep .pm-editor .ck.ck-editor {
    border: 1px solid #e5e7eb !important;
    border-radius: 10px !important;
}

::v-deep .pm-editor .ck.ck-toolbar {
    border: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    border-radius: 10px 10px 0 0 !important;
    background: #fff !important;
}

::v-deep .pm-editor .ck.ck-editor__main > .ck-editor__editable {
    border: none !important;
    border-radius: 0 0 10px 10px !important;
    padding: 12px 16px !important;
    min-height: 80px;
    color: #000 !important;
}

.pm-toolbar {
    display: grid;
    grid-template-columns: repeat(2, auto);
    grid-auto-rows: auto;
    justify-content: start;
    align-items: stretch;
    column-gap: 8px;
    row-gap: 24px;
}

.pm-tool-group {
    display: contents;
}

.pm-tool-count {
    margin-left: 6px;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
}

@media (max-width: 640px) {
    .pm-tool-lbl {
        display: none;
    }
}

::v-deep .upload .upload-draggable {
    padding: 0;
    padding-right: 3px;
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions } from 'vuex'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import UploadedFile from '@/components/Model/File/UploadedFile'
import UploadAdapter from '@/components/Model/File/UploadAdapter'
import PostTemplate from '@/components/Model/Post/PostTemplate'
import BmvButton from '@/components/UI/v2/BmvButton.vue'

export default {
    name: 'PostMessages',
    props: ['post'],
    components: {
        UploadedFile,
        PostTemplate,
        BmvButton
    },
    data() {
        return {
            isMessage: true,
            content: null,
            loading: false,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'bold',
                        'italic',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'undo',
                        'redo',
                        '|'
                    ]
                }
            },
            filesList: [],
            dropFiles: []
        }
    },
    computed: {
        isDisabled() {
            if (
                (this.content != null && this.content != '') ||
                (this.filesList != [] && this.filesList.length > 0)
            ) {
                return false
            }
            return true
        }
    },
    created() {},
    methods: {
        openFilePicker() {
            if (this.loading) return
            this.$refs.fileInput && this.$refs.fileInput.click()
        },
        onChange() {
            if (
                this.content.includes('&lt;img') &&
                this.content.includes('&gt;')
            ) {
                let images = this.content.match(/&lt;img.*?&gt;/g)
                for (let i = 0; i < images.length; i++) {
                    this.content = this.content.replace(
                        images[i],
                        this.displayHtml(images[i])
                    )
                    const changeContent = new Promise((resolve, reject) => {
                        this.content = this.content.replace(
                            images[i],
                            this.displayHtml(images[i])
                        )
                        resolve()
                    })

                    changeContent.finally(() => {
                        const src = this.content.match(/src="(.*?)"/)[1]
                        const style = this.content.match(/style="(.*?)"/)
                            ? this.content.match(/style="(.*?)"/)[1]
                            : ''
                        const width = this.content.match(/width="(.*?)"/)
                            ? this.content.match(/width="(.*?)"/)[1]
                            : ''
                        const height = this.content.match(/height=(.*?)"/)
                            ? this.content.match(/height=(.*?)"/)[1]
                            : ''
                        let image = document.querySelector(
                            'img[src="' + src + '"]'
                        )
                        image.setAttribute(
                            'style',
                            style + ` width: ${width}; ` + `height: ${height};`
                        )
                    })
                }
            }
            if (
                this.content.includes('&lt;a') &&
                this.content.includes('&lt;/a&gt;')
            ) {
                let links = this.content.match(/&lt;a.*?&lt;\/a&gt;/g)
                for (let i = 0; i < links.length; i++) {
                    this.content = this.content.replace(
                        links[i],
                        this.displayHtml(links[i], true)
                    )
                }
            }
        },
        displayHtml(message, isText = false) {
            message = message
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')

            if (!isText) message = message.replace(/<br\s*[\/]?>/gi, '')

            return message
        },

        fileDownloadUrl(file) {
            if (!file || !file.url) return '#'
            const raw = String(file.url)
            // Backend may already return an absolute URL — leave it untouched.
            if (/^(https?:|data:|blob:)/i.test(raw)) return raw
            // The backend URL is host-relative and already contains /api/{company}
            // (e.g. "/api/1/storage/message/download/<uuid>"), so prefix only the
            // backend origin — never the API base, or /api/{company} would double.
            const origin = (process.env.VUE_APP_BACKEND_URL || '').replace(/\/+$/, '')
            return `${origin}/${raw.replace(/^\/+/, '')}`
        },
        displayMessage(message) {
            if (message.content.message) {
                if (message.content.message.length > 0) {
                    return true
                }
            } else if (message.content.files) {
                if (message.content.files.length > 0) {
                    return true
                }
            }
            return false
        },
        ...mapActions('post', ['sendNewMessage', 'getPostHistories']),
        setContent(payload) {
            this.content = payload.content
        },
        onEditorReady(editor) {
            this.uploader(editor)
        },
        uploader(editor) {
            var token = window.localStorage.getItem('token')
            var baseURL = window.localStorage.getItem('baseURL')

            editor.plugins.get('FileRepository').createUploadAdapter = (
                loader
            ) => {
                return new UploadAdapter(loader, token, baseURL)
            }
        },
        postComment() {
            this.loading = true
            if (this.filesList.length == 0) {
                this.sendMessage({ message: this.content })
            } else if (this.filesList.length > 0 && this.content == null) {
                this.sendMessage({
                    files: this.filesList
                })
            } else {
                this.sendMessage({
                    message: this.content,
                    files: this.filesList
                })
            }
            this.filesList = []
            this.dropFiles = []
        },
        sendMessage(content) {
            if (
                (this.content == '' || this.content == null) &&
                this.filesList == []
            ) {
                this.$store.dispatch('toast', {
                    type: 'error',
                    title: this.$t('error.title'),
                    message: this.$t('error.validation.not_empty')
                })
                this.loading = false
            } else {
                if (
                    this.content !== '' &&
                    this.content != null &&
                    this.content.match(/<a(.*?)<\/a>/)
                ) {
                    const regex = /<a href=".+">.{20,}<\/a>/g
                    content.message = content.message.replace(
                        regex,
                        (match) => {
                            return match.replace(/<a /, '<a class="long-url" ')
                        }
                    )
                }
                this.sendNewMessage(content)
                    .then((response) => {
                        let message = response.data.message
                        message.author = {
                            name: this.$store.state.auth.user.name
                        }
                        if (
                            content.hasOwnProperty('message') &&
                            content.hasOwnProperty('files')
                        ) {
                            let messageContent = {
                                content: content.message,
                                created_at: response.data.message.created_at,
                                author: {
                                    name: this.$store.state.auth.user.name
                                }
                            }
                            this.$store.state.post.post.messages.push(
                                messageContent
                            )
                            this.$store.state.post.post.messages.push(
                                response.data.message
                            )
                        } else {
                            this.$store.state.post.post.messages.push(
                                response.data.message
                            )
                        }
                        this.content = ''
                        this.getPostHistories(this.post.id)
                        this.$store.dispatch('toast', {
                            type: 'success',
                            title: this.$t('success.title')
                        })
                    })
                    .catch((error) => {
                        this.$store.dispatch('dispatchError', error)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        },
        showFiles(dropFiles) {
            dropFiles.forEach((file) => {
                this.encodeFileToBase64(file)
            })
        },
        onFileInput(event) {
            const files = Array.from(event.target.files || [])
            if (!files.length) return
            this.dropFiles = this.dropFiles.concat(files)
            this.showFiles(files)
            event.target.value = ''
        },
        encodeFileToBase64(file) {
            this.filesList = []
            var fr = new FileReader()
            fr.onload = (e) => {
                this.filesList.push({
                    name: file.name,
                    encodedFile: e.target.result
                })
            }
            fr.readAsDataURL(file)
        }
    }
}
</script>
